
import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Database, Upload, BarChart2, Search, Filter,
  X, CheckCircle, AlertCircle, Clock, ChevronDown,
  FileText, Loader2, UploadCloud, Users, TrendingUp,
  RefreshCw, Download, Eye, Briefcase, Zap,
  Info, XCircle, PanelRightOpen, PanelRightClose,
} from "lucide-react";
import { RESUME_API_END_POINT, USER_API_END_POINT } from "@/utils/constant";
import Footer from "../Footer";
import Pagination from "./Pagination";
import { toast } from "sonner";
import ResumePreviewPanel, { avColor, initials } from "./ResumePreviewPanel";

// ─── Tabs ────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "search", label: "Search Database", icon: Search },
  { id: "upload", label: "Bulk Upload", icon: UploadCloud },
  { id: "analytics", label: "Analytics", icon: BarChart2 },
];

const VISA_OPTS = [
  { value: "all", label: "All Visa Types" },
  { value: "Permanent_Resident", label: "Permanent Resident" },
  { value: "Student_Visa", label: "Student Visa" },
  { value: "Other", label: "Other" },
  { value: "US_Citizen", label: "US Citizen" },
  { value: "EAD", label: "Employment Authorization Document" },
  { value: "Need_H1", label: "Need H-1 Visa" },
  { value: "Have_H1", label: "Have H-1 Visa" },
  { value: "TN_Permit", label: "TN Permit Holder" },
  { value: "Unspecified", label: "Unspecified" },
];

const EXP_OPTS = ["all", "0-2 yrs", "3-5 yrs", "6-10 yrs", "10+ yrs"];
const DATE_OPTS = [
  { value: "all", label: "Any upload date" },
  { value: "today", label: "Today" },
  { value: "last3days", label: "Last 3 days" },
  { value: "last7days", label: "Last 7 days" },
];

const dateMatchesFilter = (createdAt, filter) => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return false;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor(
    (startOfToday - new Date(created.getFullYear(), created.getMonth(), created.getDate())) /
    (1000 * 60 * 60 * 24)
  );
  if (filter === "today") return diffDays === 0;
  if (filter === "last3days") return diffDays >= 0 && diffDays < 3;
  if (filter === "last7days") return diffDays >= 0 && diffDays < 7;
  return true;
};

// ─── Experience bucket helper ─────────────────────────────────────────────────
const expBucket = (years) => {
  if (years <= 2) return "0-2 yrs";
  if (years <= 5) return "3-5 yrs";
  if (years <= 10) return "6-10 yrs";
  return "10+ yrs";
};

// ─── File status badge ────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  if (status === "success")
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
        <CheckCircle className="w-2.5 h-2.5" /> Saved
      </span>
    );
  if (status === "processing")
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
        <Loader2 className="w-2.5 h-2.5 animate-spin" /> Parsing…
      </span>
    );
  if (status === "skipped")
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
        <AlertCircle className="w-2.5 h-2.5" /> Duplicate
      </span>
    );
  if (status === "failed")
    return (
      <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
        <AlertCircle className="w-2.5 h-2.5" /> Failed
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
      <Clock className="w-2.5 h-2.5" /> Queued
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const InternalDBPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Tabs
  const [activeTab, setActiveTab] = useState("search");

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [visaFilter, setVisaFilter] = useState("all");
  const [expFilter, setExpFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // API candidates
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidatesError, setCandidatesError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // ── Resume preview panel ──────────────────────────────────────────────────
  const [previewCandidate, setPreviewCandidate] = useState(null);

  // Upload state
  const [fileQueue, setFileQueue] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);

  // Close preview on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") setPreviewCandidate(null);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── Fetch candidates from API ─────────────────────────────────────────────
  const fetchCandidates = useCallback(async () => {
    setLoadingCandidates(true);
    setCandidatesError(null);
    try {
      const res = await fetch(`${RESUME_API_END_POINT}`, { credentials: "include" });
      const data = await res.json();
      setCandidates(data.candidates || []);
    } catch (err) {
      setCandidatesError("Failed to load candidates. Please try again.");
    } finally {
      setLoadingCandidates(false);
    }
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filtered = candidates.filter((c) => {
    const q = searchQuery.toLowerCase();
    const skillStr = (c.skills || []).join(" ").toLowerCase();
    const nameStr = (c.name || "").toLowerCase();
    const matchQ = !q || nameStr.includes(q) || skillStr.includes(q);
    const matchVisa = visaFilter === "all";
    const matchExp = expFilter === "all" || expBucket(c.experience ?? 0) === expFilter;
    const matchDate = dateFilter === "all" || dateMatchesFilter(c.createdAt, dateFilter);
    return matchQ && matchVisa && matchExp && matchDate;
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginatedCandidates = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    setTotalItems(filtered.length);
    setCurrentPage(1);
  }, [filtered.length]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const activeFilterCount = [
    visaFilter !== "all",
    expFilter !== "all",
    dateFilter !== "all",
  ].filter(Boolean).length;

  // ── File upload handlers ──────────────────────────────────────────────────
  const addFiles = useCallback((files) => {
    const newEntries = Array.from(files).map((f) => ({
      id: Math.random().toString(36).slice(2),
      file: f,
      name: f.name,
      size: f.size,
      status: "queued",
      progress: 0,
    }));
    setFileQueue((prev) => [...prev, ...newEntries]);
    setUploadSummary(null);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const removeFile = (id) => setFileQueue((prev) => prev.filter((f) => f.id !== id));
  const clearQueue = () => {
    setFileQueue([]);
    setUploadSummary(null);
  };

  const handleUpload = async () => {
    const pending = fileQueue.filter((f) => f.status === "queued");
    if (!pending.length) return;
    setIsUploading(true);
    const formData = new FormData();
    pending.forEach((f) => formData.append("resumes", f.file));
    setFileQueue((prev) =>
      prev.map((f) =>
        f.status === "queued" ? { ...f, status: "processing", progress: 30 } : f
      )
    );
    try {
      const res = await fetch(`${RESUME_API_END_POINT}/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await res.json();
      const resultMap = {};
      (data.results || []).forEach((r) => { resultMap[r.file] = r; });
      setFileQueue((prev) =>
        prev.map((f) => {
          if (f.status !== "processing") return f;
          const result = resultMap[f.name];
          const apiStatus = result?.status;
        
return {
  ...f,
  status: apiStatus === "success" ? "success" : apiStatus === "skipped" ? "skipped" : "failed",
  progress: apiStatus === "success" ? 100 : 0,
  duplicateField: result?.duplicateField || null,
  duplicateValue: result?.duplicateValue || null,
  skipReason: result?.reason || null,
  errorMessage: result?.error || null,  // ← ADD THIS LINE
};
        })
      );
      const success = (data.results || []).filter((r) => r.status === "success").length;
      const skipped = (data.results || []).filter((r) => r.status === "skipped").length;
      const failed = (data.results || []).filter((r) => r.status === "failed").length;
      const skippedList = (data.results || [])
        .filter((r) => r.status === "skipped")
        .map((r) => ({ file: r.file, field: r.duplicateField, value: r.duplicateValue }));

      const failedList = (data.results || [])
        .filter((r) => r.status === "failed")
        .map((r) => ({ file: r.file, candidateName: r.candidateName, error: r.error }));

      setUploadSummary({ success, skipped, failed, total: data.results?.length || 0, skippedList, failedList });
      if (success > 0) fetchCandidates();
    } catch (err) {
      setFileQueue((prev) =>
        prev.map((f) =>
          f.status === "processing" ? { ...f, status: "failed", progress: 0 } : f
        )
      );
      setUploadSummary({ success: 0, skipped: 0, failed: pending.length, total: pending.length, skippedList: [], failedList: [] });
    } finally {
      setIsUploading(false);
    }
  };

  const formatSize = (bytes) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(0)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-screen bg-[#f8f9fc]">

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <div className="relative w-full overflow-hidden">
          <div className="absolute inset-0 bg-[#012760]" />
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-400 rounded-full opacity-10 blur-3xl" />
          <div className="absolute -bottom-10 right-20 w-80 h-80 bg-blue-300 rounded-full opacity-10 blur-3xl" />
          <div className="relative max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-10 md:py-12">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/50 hover:text-white text-xs font-medium mb-5 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Candidates
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300">Internal DB</span>
                  <span className="w-1 h-1 rounded-full bg-blue-400 opacity-60" />
                  <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Resume Intelligence</span>
                </div>
                <h1 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2">
                  Resume <span className="text-blue-300">Intelligence</span> Hub
                </h1>
                <p className="text-white/50 text-sm max-w-xl">
                  Search, filter and manage your proprietary resume database. Bulk upload resumes — they're auto-parsed and instantly searchable.
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                {[
                  { icon: Users, label: `${candidates.length} Resumes`, bg: "bg-white/10" },
                  { icon: CheckCircle, label: `${candidates.filter(c => c.name && c.name !== "Unknown").length} Parsed`, bg: "bg-green-500/20" },
                  { icon: AlertCircle, label: `${candidates.filter(c => !c.name || c.name === "Unknown").length} Failed`, bg: "bg-red-500/20" },
                ].map(({ icon: Icon, label, bg }) => (
                  <div key={label} className={`flex items-center gap-2 ${bg} border border-white/10 rounded-xl px-4 py-2`}>
                    <Icon className="w-3.5 h-3.5 text-white/60" />
                    <span className="text-white text-xs font-semibold">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Tab bar ───────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-100 sticky top-0 z-30 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 flex gap-0">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap
                  ${activeTab === id
                    ? "border-[#012760] text-[#012760]"
                    : "border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200"
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {id === "upload" && fileQueue.filter((f) => f.status === "queued").length > 0 && (
                  <span className="ml-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {fileQueue.filter((f) => f.status === "queued").length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Page body ─────────────────────────────────────────────────── */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-8">

          {/* ══════════════════════════════════════════════════════════════
              TAB: Search
          ══════════════════════════════════════════════════════════════ */}
          {activeTab === "search" && (
            <div>
              {/* Search bar row */}
              <div className="flex gap-3 mb-4 flex-wrap">
                <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm focus-within:border-[#012760] transition-colors">
                  <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search name, skills…"
                    className="w-full text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery("")}>
                      <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-medium transition-all shadow-sm
                    ${showFilters || activeFilterCount > 0
                      ? "border-[#012760] text-[#012760] bg-[#012760]/5"
                      : "border-gray-200 text-gray-600 bg-white hover:border-[#012760] hover:text-[#012760]"
                    }`}
                >
                  <Filter className="w-3.5 h-3.5" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="w-4 h-4 rounded-full bg-[#012760] text-white text-[9px] font-bold flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                  <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
                </button>

                <button
                  onClick={fetchCandidates}
                  className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Refresh
                </button>

                <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 bg-white hover:border-gray-300 shadow-sm transition-all">
                  <Download className="w-3.5 h-3.5" />
                  Export
                </button>

                {/* Panel toggle hint when open */}
                {previewCandidate && (
                  <button
                    onClick={() => setPreviewCandidate(null)}
                    className="flex items-center gap-2 px-4 py-2.5 border border-[#012760] rounded-xl text-sm font-medium text-[#012760] bg-[#012760]/5 shadow-sm transition-all"
                  >
                    <PanelRightClose className="w-3.5 h-3.5" />
                    Close Preview
                  </button>
                )}
              </div>

              {/* Filter panel */}
              {showFilters && (
                <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-5 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Visa Status</p>
                      <select
                        value={visaFilter}
                        onChange={(e) => setVisaFilter(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg text-sm px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#012760]/20 focus:border-[#012760]"
                      >
                        {VISA_OPTS.map((v) => (
                          <option key={v.value} value={v.value}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Experience</p>
                      <select
                        value={expFilter}
                        onChange={(e) => setExpFilter(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg text-sm px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#012760]/20 focus:border-[#012760]"
                      >
                        {EXP_OPTS.map((v) => (
                          <option key={v} value={v}>{v === "all" ? "Any Experience" : v}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Uploaded</p>
                      <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg text-sm px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#012760]/20 focus:border-[#012760]"
                      >
                        {DATE_OPTS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {activeFilterCount > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => {
                          setVisaFilter("all");
                          setExpFilter("all");
                          setDateFilter("all");
                        }}
                        className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Clear all filters
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Results header */}
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-gray-700">
                  {loadingCandidates ? (
                    <span className="flex items-center gap-2 text-gray-400">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                    </span>
                  ) : (
                    <>
                      {filtered.length.toLocaleString()}{" "}
                      <span className="font-normal text-gray-400">
                        {filtered.length === 1 ? "resume" : "resumes"} found
                      </span>
                    </>
                  )}
                </p>
                {previewCandidate && (
                  <p className="text-xs text-[#012760] font-medium flex items-center gap-1.5">
                    <PanelRightOpen className="w-3.5 h-3.5" />
                    Previewing: {previewCandidate.name || "Unknown"}
                  </p>
                )}
              </div>

              {/* Error state */}
              {candidatesError && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{candidatesError}</p>
                  <button
                    onClick={fetchCandidates}
                    className="ml-auto text-xs font-medium text-red-600 hover:text-red-800 underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* ── Layout wrapper: list shrinks when panel is open ── */}
              <div className="transition-all duration-300">
                {/* Candidate list */}
                {!loadingCandidates && filtered.length === 0 && !candidatesError ? (
                  <div className="bg-white rounded-2xl border border-gray-100 p-14 text-center shadow-sm">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <Search className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-gray-700 font-semibold mb-1">No resumes found</p>
                    <p className="text-gray-400 text-sm">Try adjusting your search or clearing filters</p>
                  </div>
                ) : (
                  <div
                    className="space-y-3 transition-all duration-300"
                    style={
                      previewCandidate
                        ? { marginRight: "calc(520px + 16px)" }
                        : {}
                    }
                  >
                    {paginatedCandidates.map((c) => {
                      const av = avColor(c.name);
                      const isUnknown = !c.name || c.name === "Unknown";
                      const displaySkills = (c.skills || []).slice(0, previewCandidate ? 2 : 4);
                      const isSelected = previewCandidate?.id === c.id;

                      return (
                        <div
                          key={c.id}
                          className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden
                            ${isSelected
                              ? "border-[#012760] ring-2 ring-[#012760]/10"
                              : "border-gray-100 hover:border-blue-100"
                            }`}
                        >
                          <div className="flex items-center gap-4 p-4">
                            {/* Avatar */}
                            <div
                              className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
                              style={{ background: av.bg, color: av.color }}
                            >
                              {initials(c.name)}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <p className={`text-sm font-semibold ${isUnknown ? "text-gray-400 italic" : "text-gray-800"}`}>
                                  {isUnknown ? "Parse Failed" : c.name}
                                </p>
                                {isUnknown && (
                                  <span className="flex items-center gap-1 text-[10px] font-medium text-red-500 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-full">
                                    <AlertCircle className="w-2.5 h-2.5" /> Failed
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mb-2">
                                {c.experience != null && (
                                  <span className="flex items-center gap-1 text-xs text-gray-500">
                                    <Briefcase className="w-3 h-3" />
                                    {c.experience} yr{c.experience !== 1 ? "s" : ""} exp
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-1.5 flex-wrap">
                                {displaySkills.map((s) => (
                                  <span
                                    key={s}
                                    className="bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3] text-[11px] font-medium px-2 py-0.5 rounded-md"
                                  >
                                    {s}
                                  </span>
                                ))}
                                {(c.skills || []).length > (previewCandidate ? 2 : 4) && (
                                  <span className="text-[11px] text-gray-400 px-2 py-0.5">
                                    +{(c.skills || []).length - (previewCandidate ? 2 : 4)} more
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Right meta */}
                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-[#f0f4ff] border-[#dde6ff] text-[#3730a3]">
                                Uploaded
                              </span>
                              <span className="text-xs text-gray-400">
                                {new Date(c.createdAt).toLocaleDateString("en-IN", {
                                  day: "numeric", month: "short", year: "numeric",
                                })}
                              </span>

                              {/* View Resume button — opens side panel */}
                              {c.resumeUrl ? (
                                <button
                                  onClick={() =>
                                    setPreviewCandidate(isSelected ? null : c)
                                  }
                                  className={`flex items-center gap-1.5 text-[11px] border rounded-lg px-3 py-1 font-medium transition-all
                                    ${isSelected
                                      ? "bg-[#012760] text-white border-[#012760]"
                                      : "text-[#012760] border-[#012760] hover:bg-[#012760] hover:text-white"
                                    }`}
                                >
                                  {isSelected ? (
                                    <><PanelRightClose className="w-3 h-3" /> Close</>
                                  ) : (
                                    <><Eye className="w-3 h-3" /> View Resume</>
                                  )}
                                </button>
                              ) : (
                                <button
                                  onClick={() => setPreviewCandidate(isSelected ? null : c)}
                                  className={`flex items-center gap-1.5 text-[11px] border rounded-lg px-3 py-1 font-medium transition-all
                                    ${isSelected
                                      ? "bg-gray-600 text-white border-gray-600"
                                      : "text-gray-500 border-gray-300 hover:bg-gray-100"
                                    }`}
                                >
                                  <Eye className="w-3 h-3" /> Info
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {filtered.length > 0 && (
                <div
                  className="transition-all duration-300"
                  style={previewCandidate ? { marginRight: "calc(520px + 16px)" } : {}}
                >
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB: Upload
          ══════════════════════════════════════════════════════════════ */}
          {activeTab === "upload" && (
            <div className="max-w-5xl mx-auto">

        
{uploadSummary && (
  <div className="rounded-2xl border mb-4 overflow-hidden">

    {/* Top banner */}
    <div className={`flex items-center gap-3 px-5 py-3
      ${uploadSummary.failed === 0 && uploadSummary.skipped === 0
        ? "bg-green-50 border-green-200"
        : "bg-yellow-50 border-yellow-200"
      }`}
    >
      {uploadSummary.failed === 0 && uploadSummary.skipped === 0
        ? <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        : <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
      }
      <div className="flex-1">
        <p className={`text-sm font-semibold ${uploadSummary.failed === 0 && uploadSummary.skipped === 0 ? "text-green-800" : "text-yellow-800"}`}>
          Upload complete — {uploadSummary.success} saved
          {uploadSummary.skipped > 0 && `, ${uploadSummary.skipped} duplicate${uploadSummary.skipped > 1 ? "s" : ""}`}
          {uploadSummary.failed > 0 && `, ${uploadSummary.failed} failed`}
        </p>
        <p className={`text-xs ${uploadSummary.failed === 0 && uploadSummary.skipped === 0 ? "text-green-600" : "text-yellow-600"}`}>
          {uploadSummary.success > 0
            ? "Saved resumes are now searchable in the Database tab."
            : "No new resumes were saved."}
        </p>
      </div>
    </div>

    {/* Skipped / duplicates */}
    {uploadSummary.skippedList?.length > 0 && (
      <div className="bg-yellow-50 border-t border-yellow-200 px-5 py-3">
        <p className="text-xs font-semibold text-yellow-800 mb-2 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          Already in database — skipped:
        </p>
        <div className="space-y-1.5">
          {uploadSummary.skippedList.map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-yellow-200 rounded-lg px-3 py-2">
              <div className="w-6 h-6 rounded bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-3.5 h-3.5 text-yellow-700" />
              </div>
              <span className="text-xs font-medium text-gray-700 truncate flex-1">{item.file}</span>
              {item.field && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300 whitespace-nowrap flex-shrink-0">
                  {item.field === "email" ? `📧 ${item.value}` : `📞 ${item.value}`}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    )}

    {/* Failed */}
    {uploadSummary.failedList?.length > 0 && (
      <div className="bg-red-50 border-t border-red-200 px-5 py-3">
        <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          Failed to upload:
        </p>
        <div className="space-y-1.5">
          {uploadSummary.failedList.map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border border-red-200 rounded-lg px-3 py-2">
              <div className="w-6 h-6 rounded bg-red-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-3.5 h-3.5 text-red-700" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-medium text-gray-700 truncate block">{item.file}</span>
                {item.candidateName && item.candidateName !== "Unknown" && (
                  <span className="text-[10px] text-gray-400">{item.candidateName}</span>
                )}
              </div>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 whitespace-nowrap flex-shrink-0 max-w-[200px] truncate"
                title={item.error}>
                {item.error}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

  </div>  
)}

              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#012760]">
                    <UploadCloud className="w-4 h-4" />
                    Bulk Resume Upload
                  </div>
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#f0f4ff] border border-[#dde6ff] text-[#3730a3]">
                    {candidates.length} in DB
                  </span>
                </div>
                <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-100">
                  <div className="flex-1 p-5">
                    <div
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all h-full flex flex-col items-center justify-center gap-3
                        ${isDragging
                          ? "border-[#012760] bg-[#f0f4ff]"
                          : "border-gray-200 bg-gray-50 hover:border-[#012760] hover:bg-[#f0f4ff]"
                        }`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#012760] flex items-center justify-center">
                        <UploadCloud className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800 mb-0.5">Drop resumes here or click to browse</p>
                        <p className="text-xs text-gray-400">AI-parsed and saved automatically</p>
                      </div>
                      <div className="flex gap-1.5 justify-center flex-wrap">
                        {["PDF", "DOCX", "DOC", "TXT"].map((fmt) => (
                          <span key={fmt} className="bg-white border border-gray-200 rounded-md px-2 py-0.5 text-[11px] font-medium text-gray-500">{fmt}</span>
                        ))}
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        accept=".pdf,.doc,.docx,.txt"
                        className="hidden"
                        onChange={(e) => addFiles(e.target.files)}
                      />
                    </div>
                  </div>
                  <div className="flex-1 p-5 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Queue {fileQueue.length > 0 ? `(${fileQueue.length})` : ""}
                      </p>
                      {fileQueue.length > 0 && (
                        <button onClick={clearQueue} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" /> Clear all
                        </button>
                      )}
                    </div>
                    {fileQueue.length === 0 && (
                      <div className="flex-1 flex flex-col items-center justify-center text-center py-8 text-gray-300">
                        <FileText className="w-8 h-8 mb-2" />
                        <p className="text-xs">No files selected yet</p>
                        <p className="text-[11px] mt-0.5">Choose or drop files on the left</p>
                      </div>
                    )}
                    {fileQueue.length > 0 && (
                      <div className="flex-1 space-y-2 overflow-y-auto max-h-52 pr-1">
                        {fileQueue.map((f) => (
                          <div key={f.id} className="flex items-center gap-3 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
                            <div className="w-7 h-7 rounded-lg bg-[#012760] flex items-center justify-center flex-shrink-0">
                              <FileText className="w-3.5 h-3.5 text-white" />
                            </div>
                            
<div className="flex-1 min-w-0">
  <p className="text-xs font-medium text-gray-700 truncate">{f.name}</p>
  <p className="text-[10px] text-gray-400">{formatSize(f.size)}</p>
  {f.status === "failed" && f.errorMessage && (
    <p className="text-[10px] text-red-500 mt-0.5 truncate" title={f.errorMessage}>
      {f.errorMessage}
    </p>
  )}
  {f.status === "skipped" && f.skipReason && (
    <p className="text-[10px] text-yellow-600 mt-0.5 truncate" title={f.skipReason}>
      {f.skipReason}
    </p>
  )}
  {f.status === "processing" && (
    <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-[#012760] to-blue-400 rounded-full transition-all duration-500"
        style={{ width: `${f.progress}%` }}
      />
    </div>
  )}
</div>
                            <StatusBadge status={f.status} />
                            {f.status === "queued" && (
                              <button onClick={() => removeFile(f.id)} className="text-gray-300 hover:text-red-400 transition-colors ml-1">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={
                        fileQueue.filter((f) => f.status === "queued").length > 0
                          ? handleUpload
                          : () => fileInputRef.current?.click()
                      }
                      disabled={isUploading}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-[#012760] text-white text-sm font-semibold rounded-xl hover:bg-[#023080] transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                    >
                      {isUploading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Uploading & Parsing…</>
                      ) : fileQueue.filter((f) => f.status === "queued").length > 0 ? (
                        <><Zap className="w-4 h-4" /> Upload {fileQueue.filter((f) => f.status === "queued").length} Resume{fileQueue.filter((f) => f.status === "queued").length > 1 ? "s" : ""}</>
                      ) : (
                        <><Upload className="w-4 h-4" /> Choose Files</>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-3 bg-[#f0f4ff] border border-[#dde6ff] rounded-2xl px-5 py-3">
                <Info className="w-4 h-4 text-[#3730a3] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#4338ca] leading-relaxed">
                  Each resume is uploaded to S3, then an AI parser extracts name, skills, experience,
                  job title, and contact info — saved to your internal database and instantly searchable.
                </p>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB: Analytics
          ══════════════════════════════════════════════════════════════ */}
          {activeTab === "analytics" && (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: "Total Resumes", value: candidates.length, sub: "in database", icon: Database, color: "text-[#012760]", bg: "bg-[#f0f4ff]" },
                  { label: "Parsed Successfully", value: candidates.filter(c => c.name && c.name !== "Unknown").length, sub: "successfully parsed", icon: CheckCircle, color: "text-green-700", bg: "bg-green-50" },
                  { label: "Failed Uploads", value: candidates.filter(c => !c.name || c.name === "Unknown").length, sub: "parse failures", icon: AlertCircle, color: "text-red-600", bg: "bg-red-50" },
                  { label: "Avg Experience", value: candidates.length ? `${(candidates.reduce((a, c) => a + (c.experience || 0), 0) / candidates.length).toFixed(1)}y` : "—", sub: "across all resumes", icon: TrendingUp, color: "text-purple-700", bg: "bg-purple-50" },
                ].map(({ label, value, sub, icon: Icon, color, bg }) => (
                  <div key={label} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                    <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center mb-3`}>
                      <Icon className={`w-4.5 h-4.5 ${color}`} style={{ width: 18, height: 18 }} />
                    </div>
                    <p className="text-2xl font-bold text-gray-800 mb-0.5">{value}</p>
                    <p className="text-xs text-gray-400">{label}</p>
                    <p className="text-[11px] text-gray-300 mt-1">{sub}</p>
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp className="w-4 h-4 text-[#012760]" />
                    <p className="text-sm font-semibold text-[#012760]">Top Skills in Database</p>
                  </div>
                  <div className="space-y-4">
                    {(() => {
                      const skillCount = {};
                      candidates.forEach(c =>
                        (c.skills || []).forEach(s => {
                          const key = s.trim();
                          skillCount[key] = (skillCount[key] || 0) + 1;
                        })
                      );
                      const top5 = Object.entries(skillCount).sort((a, b) => b[1] - a[1]).slice(0, 5);
                      const max = top5[0]?.[1] || 1;
                      const colors = ["bg-[#012760]", "bg-blue-500", "bg-indigo-500", "bg-orange-400", "bg-purple-500"];
                      return top5.map(([skill, count], i) => (
                        <div key={skill}>
                          <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-gray-700 font-medium truncate max-w-[180px]">{skill}</span>
                            <span className="text-gray-400 ml-2 flex-shrink-0">{count} resume{count !== 1 ? "s" : ""}</span>
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full ${colors[i]} rounded-full`} style={{ width: `${(count / max) * 100}%` }} />
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-5">
                    <RefreshCw className="w-4 h-4 text-[#012760]" />
                    <p className="text-sm font-semibold text-[#012760]">Recent Uploads</p>
                  </div>
                  <div className="space-y-1">
                    {[...candidates]
                      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                      .slice(0, 6)
                      .map((c) => {
                        const av = avColor(c.name);
                        const isUnknown = !c.name || c.name === "Unknown";
                        return (
                          <div key={c.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                            <div
                              className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold flex-shrink-0"
                              style={{ background: av.bg, color: av.color }}
                            >
                              {initials(c.name)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium truncate ${isUnknown ? "text-gray-400 italic" : "text-gray-700"}`}>
                                {isUnknown ? "Parse Failed" : c.name}
                              </p>
                              <p className="text-[10px] text-gray-400">
                                {new Date(c.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                              </p>
                            </div>
                            {isUnknown
                              ? <span className="text-[10px] text-red-500 font-medium">Failed</span>
                              : <span className="text-[10px] text-green-600 font-medium">Parsed</span>
                            }
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Resume preview panel (rendered at root level for correct z-index) ── */}
      {previewCandidate && (
        <ResumePreviewPanel
          candidate={previewCandidate}
          onClose={() => setPreviewCandidate(null)}
        />
      )}

      <Footer />
    </>
  );
};

export default InternalDBPage;
